import React, { createContext, useState } from 'react';

import classNames from 'classnames';
import { styled } from 'styled-components';

import { type TabProps } from './Tab';
import { ComponentSize } from '../types';

export interface TabsContext {
    type?: 'normal' | 'bordered' | 'lifted' | 'boxed';
}

// eslint-disable-next-line react-refresh/only-export-components
export const SidebarContext = createContext<TabsContext>({ type: 'normal' });

export const Tabs = ({ children, className, activeTab, type = 'normal', size = ComponentSize.MD }: TabsProps): React.JSX.Element => {
    const [currentTab, setCurrentTab] = useState(activeTab);

    const tabsClasses = classNames(
        'tabs',
        type === 'bordered' ? 'tabs-bordered' : '',
        type === 'lifted' ? 'tabs-lifted' : '',
        type === 'normal' ? 'tabs-normal' : '',
        type === 'boxed' ? 'tabs-boxed' : '',
        size === ComponentSize.XL ? 'tabs-xl' : '',
        size === ComponentSize.LG ? 'tabs-lg' : '',
        size === ComponentSize.MD ? 'tabs-md' : '',
        size === ComponentSize.SM ? 'tabs-sm' : '',
        size === ComponentSize.XS ? 'tabs-xs' : '',
        className
    );

    const handleOnTabChange = (tabId: number): void => {
        setCurrentTab(tabId);
    };

    return (
        <SidebarContext.Provider value={{ type }}>
            <TabsContainer className={tabsClasses} role="tablist">
                {children.map((tab) => {
                    return React.cloneElement(tab, {
                        checked: currentTab === tab.props.tabId,
                        onChange: (tabId: number) => {
                            handleOnTabChange(tabId);
                        }
                    });
                })}
            </TabsContainer>
        </SidebarContext.Provider>
    );
};

const TabsContainer = styled.div`
    .tab::after {
        width: max-content;
    }
`;

interface TabsProps {
    children: Array<React.ReactElement<TabProps>>;
    className?: string;
    type?: 'normal' | 'bordered' | 'lifted' | 'boxed';
    size?: ComponentSize;
    activeTab?: number;
}

export default Tabs;
